'use client';

/**
 * BottomNavWithFAB -- combines BottomNav + QuickAddSheet.
 *
 * Mobile: the center "Add" tab in BottomNav opens the sheet.
 * Desktop: renders a standalone FAB button (bottom-right) since BottomNav is hidden.
 *
 * S3.8: Shows a "Resume" badge on the FAB when a draft of pending items was
 * saved in sessionStorage (i.e. the user closed the sheet mid-staging).
 */

import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { BottomNav } from './bottom-nav';
import { QuickAddSheet } from '@/components/quick-add/quick-add-sheet';

const DRAFT_KEY = 'favely:quick-add-draft';

function checkDraftExists(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.pendingItems) && parsed.pendingItems.length > 0;
  } catch {
    return false;
  }
}

export function BottomNavWithFAB() {
  const [open, setOpen] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  // Check for a saved draft on mount (client-only)
  useEffect(() => {
    setHasDraft(checkDraftExists());
  }, []);

  // Re-check after the sheet closes so the badge reflects the new state
  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) {
      // Small delay to let the sheet's onOpenChange handler write the draft first
      setTimeout(() => setHasDraft(checkDraftExists()), 50);
    }
  }

  return (
    <>
      {/* Mobile: bottom nav with integrated add button */}
      <BottomNav onAddPress={() => setOpen(true)} hasDraft={hasDraft} />

      {/* Desktop: floating add button (bottom nav is hidden on md+) */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex fixed bottom-6 right-6 z-40 h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all"
        aria-label={hasDraft ? 'Resume adding items' : 'Quick add an item'}
      >
        <Plus className="h-6 w-6" strokeWidth={2.5} />
        {hasDraft && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-accent-foreground text-[9px] font-bold flex items-center justify-center">
            !
          </span>
        )}
      </button>

      <QuickAddSheet open={open} onOpenChange={handleOpenChange} />
    </>
  );
}
